import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { OK, BAD, WARN, ACCENT, tableSurfaces } from '../../lib/uiTokens';
import { fmt, fmtKc } from '../../lib/formatters';
import PacingBar from './PacingBar';

/**
 * Detailní P&L. Čte se shora dolů jako výsledovka, ne jako seznam: tržby,
 * náklady se znaménkem `−`, mezisoučty s `=` a EBITDA **přilepená ke spodní
 * hraně karty**, aby neodrolovala pryč.
 *
 * Co se změnilo ve 3.480.0 (sedm sloupců → pět):
 *  - „Poměrný plán" byl vlastní sloupec, ačkoli je to jen plán × uplynulá část
 *    měsíce. Jde do titulku pruhu tempa, kde ho značka „dnes" už kreslí.
 *  - „Typ" (FIX/VAR) a „Stav dat" jdou k názvu položky jako meta. Devět badge
 *    „OK" pod sebou nikomu nic neřeklo a sloupec „Stav dat" navíc střídal badge
 *    (na mezisoučtech) a odchylku v % (na položkách), takže neměl jeden význam.
 *  - Hlavička hodnotového sloupce **se mění s režimem** („Zaúčtováno" /
 *    „Projekce měsíce"). Dřív pořád stálo „Skutečnost", i když v ní bylo něco jiného.
 *
 * Barvu nesou **dva stavy** — lepší a horší než plán. Střední pásmo zůstává
 * v textové barvě a oranžová znamená výhradně „chybí doklad, je to odhad".
 * `SKUPINA_BARVA` (tržby tyrkysově, personál fialově, přímé oranžově) zmizela:
 * oranžová tím značila zároveň skupinu, odhad, tržby pozadu i lock.
 *
 * Na mobilu se tabulka **přeskládá do bloků** přes celou šířku (norma §1c) —
 * sedm sloupců na 360 px vyjede z displeje a ani jeden se nedá přečíst.
 */

const GRID = 'minmax(0,1fr) 132px 118px 200px 104px';

const Head = ({ children, align = 'right', barevne }) => (
    <div className="text-[10px] font-bold uppercase"
        style={{
            letterSpacing: '0.05em', color: barevne ? ACCENT : 'var(--text-secondary)',
            textAlign: align, padding: align === 'right' ? '0 14px 0 0' : '0 0 0 14px',
        }}>{children}</div>
);

/** Odchylka v %: barvu dostane jen jednoznačný problém nebo jednoznačný zisk. */
function Odchylka({ pct, jeVyhoda, vyrazna }) {
    if (pct == null) return <div />;
    const dobra = jeVyhoda ? pct >= 0 : pct <= 0;
    // U nákladů, které rostou ruku v ruce s tržbami, není pár procent nález —
    // barví se teprve to, co je vidět (nad 3 %), jinak zůstává text neutrální.
    const barva = Math.abs(pct) < 3 && !vyrazna ? 'var(--text-secondary)' : (dobra ? OK : BAD);
    return (
        <div className="text-right tabular-nums text-[12.5px]"
            style={{ color: barva, fontWeight: Math.abs(pct) >= 3 ? 700 : 400, padding: '0 14px 0 0' }}>
            {pct > 0 ? '+' : ''}{pct.toFixed(1).replace('.', ',')} %
        </div>
    );
}

/** Meta pod/za názvem: typ nákladu, podíl na tržbách, chybějící doklady. */
function Meta({ typ, pctTrzeb, ceka, stav }) {
    const kusy = [];
    if (typ) kusy.push(<span key="t">{typ === 'fix' ? 'fix' : 'var'}</span>);
    if (pctTrzeb != null) kusy.push(<span key="p">{String(pctTrzeb).replace('.', ',')} % z tržeb</span>);
    return (
        <>
            {kusy.length > 0 && (
                <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>
                    {kusy.map((k, i) => <span key={i}>{i > 0 ? ' · ' : ''}{k}</span>)}
                </span>
            )}
            {ceka > 0 && (
                <span className="inline-flex items-center gap-1 text-[10.5px]" style={{ color: WARN }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: WARN }} />
                    {ceka} {ceka === 1 ? 'doklad chybí' : (ceka <= 4 ? 'doklady chybí' : 'dokladů chybí')}
                </span>
            )}
            {!ceka && stav === 'ceka' && (
                <span className="inline-flex items-center gap-1 text-[10.5px]" style={{ color: WARN }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: WARN }} />
                    nic nezaúčtováno
                </span>
            )}
        </>
    );
}

export default function PnlTabulka({ sekce, pacing, souhrn, cardStyle, isDark, rezim = 'projekce', isMobile = false }) {
    const [open, setOpen] = useState({});
    if (!sekce) return null;

    const surf = tableSurfaces(isDark);
    const jeProj = rezim === 'projekce';
    const podil = pacing?.podil ?? 0;
    const trzbyRef = jeProj ? souhrn?.ciste_trzby_projekce : souhrn?.ciste_trzby;
    const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

    /** Hodnota, tempo a odchylka řádku/mezisoučtu dle režimu — jedno místo pro obojí. */
    const cti = (x, jeTrzba) => {
        const hodnota = jeProj ? x.projekce : x.skutecnost;
        const plan = x.aop_plan;
        const tempo = plan ? (jeProj ? (x.projekce_tempo ?? (x.projekce / plan)) : (x.tempo ?? x.skutecnost / plan)) : null;
        return {
            hodnota,
            // Nulový plán je totéž co žádný (stejně to čte i backend) — „plán 0 Kč"
            // by vypadalo jako naplánovaná nula, ne jako nenaplánovaná položka.
            plan: plan || null,
            tempo,
            tilde: jeProj && (x.je_projekce ?? (x.projekce != null && Math.abs((x.projekce ?? 0) - x.skutecnost) >= 0.005)),
            pct: jeProj ? x.projekce_variance_pct : x.variance_pace_pct,
            podilRef: jeProj ? 1 : podil,
            jeVyhoda: jeTrzba,
            pctTrzeb: (!jeTrzba && trzbyRef && Math.abs(hodnota) > 0.005)
                ? Math.round(hodnota / trzbyRef * 1000) / 10 : null,
        };
    };

    // ── Mobil: bloky přes celou šířku, žádný vodorovný scroll ───────────────
    if (isMobile) {
        const blok = (nazev, x, jeTrzba, { detail = false, klic, rozbalitelne = false } = {}) => {
            const d = cti(x, jeTrzba);
            const zvyrazni = d.pct != null && Math.abs(d.pct) >= 3 && !(jeTrzba ? d.pct >= 0 : d.pct <= 0);
            return (
                <div key={klic} onClick={rozbalitelne ? () => toggle(klic) : undefined}
                    style={{
                        padding: detail ? '9px 0 9px 14px' : '11px 0 9px',
                        borderBottom: '1px solid var(--border-default)',
                        borderLeft: detail ? `2px solid ${ACCENT}59` : undefined,
                        minHeight: detail ? 44 : 56,
                        cursor: rozbalitelne ? 'pointer' : undefined,
                    }}>
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 min-w-0"
                            style={{ fontSize: detail ? 12.5 : 13.5, fontWeight: detail ? 400 : 700 }}>
                            {rozbalitelne && (
                                <ChevronRight size={13} style={{ color: ACCENT, flexShrink: 0, transition: '.18s',
                                    transform: open[klic] ? 'rotate(90deg)' : 'none' }} />
                            )}
                            <span className="truncate">{jeTrzba ? nazev : `− ${nazev}`}</span>
                        </span>
                        <Odchylka pct={d.pct} jeVyhoda={jeTrzba} vyrazna={zvyrazni} />
                    </div>
                    <div className="tabular-nums" style={{ fontSize: detail ? 15 : 21, fontWeight: detail ? 700 : 800, marginTop: 2,
                        color: zvyrazni ? BAD : 'var(--text-primary)' }}>
                        {d.tilde ? '~' : ''}{fmt(d.hodnota)}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                        {d.plan != null && <span>plán {fmtKc(d.plan)}</span>}
                        <Meta typ={x.typ_fvs} pctTrzeb={d.pctTrzeb} ceka={x.ceka_dokladu} stav={x.stav} />
                    </div>
                    {!detail && d.tempo != null && (
                        <div className="mt-2">
                            <PacingBar tempo={d.tempo} podil={d.podilRef} color={ACCENT} isDark={isDark} jeVyhoda={jeTrzba} />
                        </div>
                    )}
                </div>
            );
        };

        const bloky = [];
        sekce.forEach(s => {
            const klic = `${s.skupina}::${s.nazev}`;
            const jeTrzba = s.skupina === 'trzby';
            bloky.push(blok(s.nazev, s.mezisoucet, jeTrzba, { klic, rozbalitelne: s.radky.length > 1 }));
            if (open[klic]) {
                s.radky.forEach((r, i) => bloky.push(blok(r.nazev, r, jeTrzba, { detail: true, klic: `${klic}-${i}` })));
            }
            if (s.skupina === 'prime_naklady' && souhrn) {
                bloky.push(soucetBlok('= Hrubá marže', jeProj ? souhrn.hruba_marze_projekce : souhrn.hruba_marze,
                    trzbyRef, jeProj));
            }
        });

        return (
            <div>
                <div className="text-[9.5px] font-bold uppercase mb-1"
                    style={{ letterSpacing: '0.12em', color: 'var(--text-secondary)' }}>
                    P&amp;L{sekce.some(s => s.radky.length > 1) ? ' · klepnutím rozbalíš položky' : ''}
                </div>
                {bloky}
                {souhrn && (
                    <div className="mt-3 rounded-[14px] px-3.5 py-3 flex items-baseline justify-between gap-2"
                        style={{ background: surf.groupBg, border: '1px solid var(--border-default)', borderTop: `2px solid ${ACCENT}` }}>
                        <div>
                            <div className="text-[13px] font-extrabold" style={{ color: ACCENT }}>═ EBITDA</div>
                            <div className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>
                                {ebitdaPopis(souhrn, jeProj)}
                            </div>
                        </div>
                        <div className="text-[19px] font-extrabold tabular-nums" style={{ color: ACCENT }}>
                            {jeProj ? '~' : ''}{fmt(jeProj ? souhrn.ebitda_projekce : souhrn.ebitda)}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── Desktop: pět sloupců ────────────────────────────────────────────────
    const radek = (nazev, x, jeTrzba, { detail = false, klic, rozbalitelne = false } = {}) => {
        const d = cti(x, jeTrzba);
        const zvyrazni = d.pct != null && Math.abs(d.pct) >= 3 && !(jeTrzba ? d.pct >= 0 : d.pct <= 0);
        return (
            <div key={klic} onClick={rozbalitelne ? () => toggle(klic) : undefined}
                style={{
                    display: 'grid', gridTemplateColumns: GRID, alignItems: 'center',
                    height: detail ? 34 : 40, cursor: rozbalitelne ? 'pointer' : undefined,
                    background: detail ? 'rgba(0,0,0,0.14)' : surf.groupBg,
                    borderBottom: '1px solid var(--border-subtle)',
                    opacity: x.stav === 'bez_planu' ? 0.55 : 1,
                }}>
                <div className="flex items-center gap-2 min-w-0" style={{ padding: detail ? '0 14px 0 38px' : '0 14px' }}>
                    {rozbalitelne && (
                        <ChevronRight size={14} style={{ color: ACCENT, flexShrink: 0, transition: '.18s',
                            transform: open[klic] ? 'rotate(90deg)' : 'none' }} />
                    )}
                    <span className="truncate" style={{ fontSize: detail ? 13 : 13.5, fontWeight: detail ? 400 : 700,
                        color: detail ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                        {jeTrzba ? nazev : `− ${nazev}`}
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                        <Meta typ={detail ? x.typ_fvs : null} pctTrzeb={detail ? null : d.pctTrzeb}
                            ceka={x.ceka_dokladu} stav={x.stav} />
                    </span>
                </div>
                <div className="text-right tabular-nums whitespace-nowrap"
                    style={{ padding: '0 14px 0 0', fontSize: detail ? 13 : 13.5, fontWeight: detail ? 400 : 700,
                        color: zvyrazni ? BAD : 'var(--text-primary)' }}>
                    {d.tilde ? '~' : ''}{fmt(d.hodnota)}
                </div>
                <div className="text-right tabular-nums text-[12.5px] whitespace-nowrap"
                    style={{ padding: '0 14px 0 0', color: 'var(--text-secondary)' }}>{fmt(d.plan)}</div>
                <div style={{ padding: '0 14px' }}
                    title={d.plan != null ? `poměrný plán k dnešku ${fmt(x.pomerny_plan)} Kč` : undefined}>
                    <PacingBar tempo={d.tempo} podil={d.podilRef} color={ACCENT} isDark={isDark} jeVyhoda={jeTrzba} />
                </div>
                <Odchylka pct={d.pct} jeVyhoda={jeTrzba} vyrazna={zvyrazni} />
            </div>
        );
    };

    const telo = [];
    sekce.forEach(s => {
        const klic = `${s.skupina}::${s.nazev}`;
        const jeTrzba = s.skupina === 'trzby';
        telo.push(radek(s.nazev, s.mezisoucet, jeTrzba, { klic, rozbalitelne: s.radky.length > 1 }));
        if (open[klic]) {
            s.radky.forEach((r, i) => telo.push(radek(r.nazev, r, jeTrzba, { detail: true, klic: `${klic}-${i}` })));
        }
        if (s.skupina === 'prime_naklady' && souhrn) {
            telo.push(
                <div key="marze" style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', height: 40,
                    background: surf.strongBg, borderLeft: `3px solid ${ACCENT}`, borderBottom: '1px solid var(--border-default)' }}>
                    <div style={{ padding: '0 14px' }} className="text-[13.5px] font-bold">
                        = Hrubá marže{' '}
                        <span className="text-[10.5px] font-normal" style={{ color: 'var(--text-secondary)' }}>
                            {marzePopis(souhrn, trzbyRef, jeProj)}
                        </span>
                    </div>
                    <div className="text-right tabular-nums text-[13.5px] font-bold whitespace-nowrap" style={{ padding: '0 14px 0 0' }}>
                        {jeProj ? '~' : ''}{fmt(jeProj ? souhrn.hruba_marze_projekce : souhrn.hruba_marze)}
                    </div>
                    <div /><div /><div />
                </div>
            );
        }
    });

    return (
        <div className="rounded-2xl overflow-hidden flex flex-col flex-1 min-h-0" style={cardStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', height: 34, flexShrink: 0,
                background: surf.headBg, borderBottom: '1px solid var(--border-default)' }}>
                <Head align="left">Položka</Head>
                <Head barevne>{jeProj ? 'Projekce měsíce' : 'Zaúčtováno'}</Head>
                <Head>Plán měsíc</Head>
                <Head align="left">Tempo plnění (▎ dnes)</Head>
                <Head>Odchylka</Head>
            </div>

            <div className="overflow-auto min-h-0" style={{ flex: 1 }}>{telo}</div>

            {/* EBITDA drží spodní hranu karty — je to výsledek, nesmí odrolovat pryč. */}
            {souhrn && (
                <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', height: 46, flexShrink: 0,
                    background: surf.groupBg, borderTop: `2px solid ${ACCENT}` }}>
                    <div style={{ padding: '0 14px' }} className="text-[14.5px] font-extrabold" >
                        <span style={{ color: ACCENT }}>═ EBITDA</span>{' '}
                        <span className="text-[11px] font-normal" style={{ color: 'var(--text-secondary)' }}>
                            {ebitdaPopis(souhrn, jeProj)}
                        </span>
                    </div>
                    <div className="text-right tabular-nums text-[15px] font-extrabold whitespace-nowrap"
                        style={{ padding: '0 14px 0 0', color: ACCENT }}>
                        {jeProj ? '~' : ''}{fmt(jeProj ? souhrn.ebitda_projekce : souhrn.ebitda)}
                    </div>
                    <div className="text-right tabular-nums text-[12.5px] whitespace-nowrap"
                        style={{ padding: '0 14px 0 0', color: 'var(--text-secondary)' }}>{fmt(souhrn.ebitda_plan)}</div>
                    <div style={{ padding: '0 14px' }}>
                        <PacingBar tempo={souhrn.ebitda_plan ? (jeProj ? souhrn.ebitda_projekce : souhrn.ebitda) / souhrn.ebitda_plan : null}
                            podil={jeProj ? 1 : podil} color={ACCENT} isDark={isDark} jeVyhoda />
                    </div>
                    <Odchylka pct={ebitdaOdchylka(souhrn, jeProj, podil)} jeVyhoda vyrazna />
                </div>
            )}
        </div>
    );
}

/** Mobilní varianta mezisoučtu (Hrubá marže) — karta s akcentní linkou vlevo. */
function soucetBlok(label, hodnota, trzbyRef, jeProj) {
    const pct = trzbyRef ? Math.round(hodnota / trzbyRef * 1000) / 10 : null;
    return (
        <div key={label} className="my-2.5 px-3 py-2.5 rounded-[11px] flex items-baseline justify-between gap-2"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-default)', borderLeft: `3px solid ${ACCENT}` }}>
            <div>
                <div className="text-[12.5px] font-bold">{label}</div>
                {pct != null && (
                    <div className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>
                        {String(pct).replace('.', ',')} % z tržeb
                    </div>
                )}
            </div>
            <div className="text-[17px] font-extrabold tabular-nums">{jeProj ? '~' : ''}{fmt(hodnota)}</div>
        </div>
    );
}

const marzePopis = (souhrn, trzbyRef, jeProj) => {
    const h = jeProj ? souhrn.hruba_marze_projekce : souhrn.hruba_marze;
    if (!trzbyRef) return '';
    return `${String(Math.round(h / trzbyRef * 1000) / 10).replace('.', ',')} % z tržeb`;
};

const ebitdaPopis = (souhrn, jeProj) => {
    const pct = jeProj ? souhrn.ebitda_projekce_pct : souhrn.ebitda_pct;
    const kusy = [];
    if (pct != null) kusy.push(`${String(pct).replace('.', ',')} % z tržeb`);
    if (souhrn.ebitda_plan_pct != null) kusy.push(`plán ${String(souhrn.ebitda_plan_pct).replace('.', ',')} %`);
    return kusy.join(' · ');
};

/** Zaúčtovaná EBITDA se poměřuje s poměrným plánem, projekce s plánem měsíce. */
const ebitdaOdchylka = (souhrn, jeProj, podil) => {
    const plan = souhrn.ebitda_plan;
    if (!plan) return null;
    const ref = jeProj ? plan : plan * podil;
    if (!ref) return null;
    const val = jeProj ? souhrn.ebitda_projekce : souhrn.ebitda;
    return Math.round((val - ref) / Math.abs(ref) * 1000) / 10;
};
